import styles from "./index.module.css";
import "./index.css";
import defaultLinkImg from "Images/file.png";

import React from "react";
import DataComponent from "Core/components/DataComponent";
import PropTypes from "prop-types";
import {connect} from "react-redux";
import {getGlobalActions} from "Core/helpers/redux";
import * as actions from "./actions";
import {getArray, getString, isset} from 'Core/helpers/data';
import {sortBy} from "lodash";
import {AsyncMountError} from "Core/errors";
import {selectors} from 'Core/store/reducers';

/**
 * Redux 'mapStateToProps' function
 *
 * @param {object} state - Redux entire store state.
 * @return {Object<string, any>} Mapped props that can be used in component.
 */
const mapStateToProps = state => ({
	appLocale: selectors.i18n.getLocale(state),
});

class HelpDocumentsComponent extends DataComponent {
	constructor(props) {
		super(props, {
			/**
			 * @typedef {Object} HelpDocument
			 * @property {string} id - Document's guid.
			 * @property {boolean} hasImage - Flag that specifies if document should have a thumbnail image. If this is 
			 * true but the image is not specified or cannot be loaded, default image (Images/file.png) will be rendered.
			 * @property {string} link - Document's URL path relative to the app root URL (begins with '/').
			 * @property {string} imageLink - Thumbnail's URL path relative to the app root URL (begins with '/').
			 * @property {string} title - Document's title.
			 * @property {string} description - Document's description.
			 * @property {number} ordinal - Position of the document in the list.
			 *
			 * @type {HelpDocument[]}
			 */
			data: [],
		}, {
			domPrefix: 'help-documents-component',
			disableLoad: true,
		});

		// Data loading methods
		this.loadDocuments = this.loadDocuments.bind(this);
	}
	
	/**
	 * Replacement for default 'componentDidMount' method that will return a promise
	 * @note This method should be used instead of the default 'componentDidMount' when you need to have async calls in
	 * your 'componentDidMount'.
	 * @important Please do not forget to decrease the value of this.mountCount once async calls finish.
	 *
	 * @param {boolean} [override=false] - Flag that determines if this method should be executed in the 'override' mode.
	 * @note Override mode is reserved for calls by the child 'componentDidMount' methods that override this method to
	 * enable overriding the data loading functionality but still executing the base component's 'componentDidMount' that
	 * handles core functionality like adding registered event listeners.
	 * @return {Promise<number|undefined>} Promise that will resolve with the updated mount count that
	 * will be set in the 'componentDidMount' method or undefined for default functionality where 'componentDidMount'
	 * will just reset the mount count to zero.
	 * @throws {AsyncMountError} Promise can reject with the AsyncMountError in which case another
	 * 'asyncComponentDidMount' will be called if mount count is greater than zero.
	 */
	async asyncComponentDidMount(override = false) {
		// If this method was not called by the override method from the child class
		if (!override) await super.asyncComponentDidMount(true).then(() => this.loadDocuments());
		// If this method was called by the override method from the child class
		else await super.asyncComponentDidMount(override);
	}

	/** @inheritDoc */
	componentDidUpdate(prevProps, prevState, snapshot) {
		return super.componentDidUpdate(prevProps, prevState, snapshot)
			.then(async () => {
				const locale = getString(this.props, 'appLocale.locale');
				const prevLocale = getString(prevProps, 'appLocale.locale');

				if (locale !== prevLocale) await this.loadDocuments();
			})
			.then(() => this.state);
	}

	/**
	 * Loads help documents from the API.
	 * @return {Promise<Object>}
	 */
	loadDocuments() {
		const {loadingSelector, fetchHelpDocumentsAction} = this.props;

		return this.executeAbortableAction(fetchHelpDocumentsAction, loadingSelector)
			.then(response => {
				if (isset(response)) return this.defaultLoad(sortBy(getArray(response, 'data'), ['ordinal']));
				else throw new AsyncMountError();
			});
	}
	
	render() {
		const {className, itemClassName} = this.props;
		/** @type {HelpDocument[]} */
		const data = getArray(this.getData());
		
		return (
			<div 
				id={this.getDomId()} 
				className={`documents ${this.getOption('domPrefix')} ${className} ${styles['wrapper']}`}
			>
				{data.map(i =>
					<div key={i.id} className={`document ${styles['item']} ${itemClassName}`}>
						<a
							className={`document ${styles['cell']} ${styles['imageWrapper']}`}
							href={i.link}
							target="_blank"
							rel="noopener noreferrer"
						>
							{
								i.hasImage ?
									<img
										className={`document-icon ${styles['image']}`}
										src={i.imageLink}
										alt=""
										onError={e => {
											e.target.className = `document-default-icon ${getString(e.target, 'className')}`;
											e.target.src = defaultLinkImg;
											e.onerror = null;
										}}
										onLoad={e => {
											e.target.className = getString(e.target, 'className')
												.replace('document-default-icon', '');
										}}
									/>
									:
									<img className={`document-default-icon ${styles['image']}`} src={defaultLinkImg} alt="" />
							}
						</a>

						<a
							className={`document ${styles['cell']} ${styles['contentWrapper']}`}
							href={i.link}
							target="_blank"
							rel="noopener noreferrer"
						>
							<div className={`${styles['content']}`}>
								<div className={styles['label']}> {i.title}</div>
								{i.description ? <div className={styles['description']}> {i.description}</div> : null}
							</div>
						</a>
					</div>
				)}
			</div>
		);
	}
}

/**
 * Define component's own props that can be passed to it by parent components
 */
HelpDocumentsComponent.propTypes = {
	id: PropTypes.string,
	className: PropTypes.string,
	itemClassName: PropTypes.string,
	loadingSelector: PropTypes.string,
};

/**
 * Define component default values for own props
 */
HelpDocumentsComponent.defaultProps = {
	id: '',
	className: '',
	itemClassName: '',
	loadingSelector: '.layout',
};

export default connect(mapStateToProps, getGlobalActions(actions))(HelpDocumentsComponent);